import { useState, useEffect } from 'react';
import { api } from '@/App';
import { useTheme } from '@/contexts/ThemeContext';
import { toast } from 'sonner';
import { 
  Globe, Search, Sparkles, AlertTriangle, Shield, Plus, Trash2, CheckCircle, 
  Link2, Server, FileSpreadsheet, Filter, X, Eye, ExternalLink, ChevronDown, Activity,
  Lightbulb, ArrowRight, Download, Loader2, Radar
} from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Switch } from '@/components/ui/switch';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";

export default function EASM() {
  const { isDarkMode } = useTheme();
  const [assets, setAssets] = useState([]);
  const [domains, setDomains] = useState([]);
  const [threats, setThreats] = useState([]);
  const [internalAssets, setInternalAssets] = useState([]);
  const [loading, setLoading] = useState(true);
  const [discovering, setDiscovering] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');
  const [activeTab, setActiveTab] = useState('assets');
  const [addDomainDialogOpen, setAddDomainDialogOpen] = useState(false);
  const [mapAssetDialogOpen, setMapAssetDialogOpen] = useState(false);
  const [deleteDomainDialogOpen, setDeleteDomainDialogOpen] = useState(false);
  const [assetDetailOpen, setAssetDetailOpen] = useState(false);
  const [domainDetailOpen, setDomainDetailOpen] = useState(false);
  const [selectedAsset, setSelectedAsset] = useState(null);
  const [selectedDomain, setSelectedDomain] = useState(null);
  const [newDomain, setNewDomain] = useState({ domain: '', description: '' });
  const [selectedAssetToMap, setSelectedAssetToMap] = useState('');
  const [addingDomain, setAddingDomain] = useState(false);
  
  // Filters
  const [exposureFilter, setExposureFilter] = useState('all');
  const [domainFilter, setDomainFilter] = useState('all');
  const [shadowItFilter, setShadowItFilter] = useState('all');
  
  // Clickable tile filter state
  const [tileFilter, setTileFilter] = useState(null);
  
  // Enterprise EASM state
  const [easmTarget, setEasmTarget] = useState('');
  const [runningEasmScan, setRunningEasmScan] = useState(false);
  const [easmDeepScan, setEasmDeepScan] = useState(false);
  const [easmHistory, setEasmHistory] = useState([]);

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      const [assetsRes, domainsRes, internalAssetsRes, threatsRes, easmHistoryRes] = await Promise.all([
        api.get('/easm/assets'),
        api.get('/easm/domains').catch(() => ({ data: [] })),
        api.get('/assets'),
        api.get('/threats').catch(() => ({ data: [] })),
        api.get('/easm/enterprise/history?limit=10').catch(() => ({ data: [] }))
      ]);
      // Handle both array and object responses for assets
      const assetsData = assetsRes.data?.assets || assetsRes.data || [];
      setAssets(Array.isArray(assetsData) ? assetsData : []);
      // Handle both array and object responses for domains
      const domainsData = domainsRes.data?.domains || domainsRes.data || [];
      setDomains(Array.isArray(domainsData) ? domainsData : []);
      setInternalAssets(internalAssetsRes.data);
      setThreats(threatsRes.data);
      setEasmHistory(Array.isArray(easmHistoryRes.data) ? easmHistoryRes.data : []);
    } catch (error) {
      toast.error('Failed to fetch EASM data');
    } finally {
      setLoading(false);
    }
  };

  const handleDiscovery = async () => {
    setDiscovering(true);
    try {
      const response = await api.post('/easm/discover');
      toast.success(response.data.message);
      fetchData();
    } catch (error) {
      toast.error('Failed to discover assets');
    } finally {
      setDiscovering(false);
    }
  };

  const handleEnterpriseEasmScan = async () => {
    if (!easmTarget) {
      toast.error('Please enter a target domain');
      return;
    }
    
    setRunningEasmScan(true);
    try {
      const response = await api.post('/easm/enterprise/scan', {
        target_domain: easmTarget,
        deep_scan: easmDeepScan
      });
      
      toast.success(`EASM scan completed: ${response.data.assets_count} assets discovered. Risk Score: ${response.data.risk_score?.toFixed(1)}`);
      fetchData();
      setEasmTarget('');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'EASM scan failed');
    } finally {
      setRunningEasmScan(false);
    }
  };

  const handleAddDomain = async (e) => {
    e.preventDefault();
    if (!newDomain.domain) {
      toast.error('Please enter a domain');
      return;
    }

    setAddingDomain(true);
    try {
      // Add the domain first
      const domainResponse = await api.post('/easm/domains', newDomain);
      toast.success('Domain added for monitoring');
      
      // Automatically trigger an Enterprise EASM scan for the newly added domain
      toast.info('Initiating attack surface scan for new domain...');
      try {
        const scanResponse = await api.post('/easm/enterprise/scan', {
          target_domain: newDomain.domain,
          deep_scan: false // Use quick scan for initial discovery
        });
        toast.success(`Attack surface scan completed: ${scanResponse.data.assets_count} assets discovered`);
      } catch (scanError) {
        console.log('Auto-scan note:', scanError.response?.data?.detail || 'Scan queued');
        // Don't show error for scan - it's a bonus feature
      }
      
      setAddDomainDialogOpen(false);
      setNewDomain({ domain: '', description: '' });
      fetchData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to add domain');
    } finally {
      setAddingDomain(false);
    }
  };

  const handleDeleteDomain = async () => {
    if (!selectedDomain) return;

    try {
      await api.delete(`/easm/domains/${selectedDomain.id}`);
      toast.success('Domain removed from monitoring');
      setDeleteDomainDialogOpen(false);
      setSelectedDomain(null);
      fetchData();
    } catch (error) {
      toast.error('Failed to remove domain');
    }
  };

  const handleVerifyDomain = async (domain) => {
    try {
      await api.post(`/easm/domains/${domain.id}/verify`);
      toast.success('Domain verified successfully');
      fetchData();
    } catch (error) {
      toast.error('Failed to verify domain');
    }
  };

  const handleMapAsset = async () => {
    if (!selectedDomain || !selectedAssetToMap) {
      toast.error('Please select an asset');
      return;
    }

    try {
      await api.post(`/easm/map-asset?domain_id=${selectedDomain.id}&asset_id=${selectedAssetToMap}`);
      toast.success('Domain mapped to asset');
      setMapAssetDialogOpen(false);
      setSelectedAssetToMap('');
      fetchData();
    } catch (error) {
      toast.error('Failed to map domain to asset');
    }
  };

  // Promote external asset to internal inventory
  const promoteExternalAsset = async (asset) => {
    try {
      await api.post('/assets', {
        name: asset.domain || asset.name || 'Unnamed Asset',
        type: 'web', // Use 'web' as default type for external assets (valid enum value)
        url: asset.domain?.startsWith('http') ? asset.domain : `https://${asset.domain}`,
        ip_address: asset.ip_address || '',
        description: `Promoted from EASM discovery. Asset Type: ${asset.asset_type || 'Unknown'}. Exposure Score: ${asset.exposure_score || 'N/A'}`,
        category: 'external',
        exposure_level: asset.exposure_score >= 80 ? 'critical' : asset.exposure_score >= 60 ? 'high' : asset.exposure_score >= 40 ? 'medium' : 'low',
        tags: ['easm-discovered', 'external']
      });
      toast.success('Asset promoted to inventory');
      fetchData();
    } catch (error) {
      toast.error('Failed to promote asset');
    }
  };

  // Delete external asset
  const deleteExternalAsset = async (assetId) => {
    if (!window.confirm('Are you sure you want to delete this external asset?')) return;
    
    try {
      await api.delete(`/external-assets/${assetId}`);
      toast.success('External asset deleted');
      fetchData();
    } catch (error) {
      toast.error('Failed to delete asset');
    }
  };

  const openAssetDetail = (asset) => {
    setSelectedAsset(asset);
    setAssetDetailOpen(true);
  };

  const openDomainDetail = (domain) => {
    setSelectedDomain(domain);
    setDomainDetailOpen(true);
  };

  // Get assets for a specific domain
  const getAssetsForDomain = (domainName) => {
    return assets.filter(a => a.domain && a.domain.includes(domainName));
  };

  // Get threats related to a domain
  const getThreatsForDomain = (domainName) => {
    return threats.filter(t => 
      t.affected_assets?.some(assetId => {
        const asset = assets.find(a => a.id === assetId);
        return asset && asset.domain?.includes(domainName);
      }) || t.source?.includes(domainName)
    );
  };

  const getExposureColor = (score) => {
    if (score >= 80) return 'bg-red-500/20 text-red-400 border-red-500/30';
    if (score >= 60) return 'bg-orange-500/20 text-orange-400 border-orange-500/30';
    if (score >= 40) return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
    return 'bg-green-500/20 text-green-400 border-green-500/30';
  };

  const getExposureLabel = (score) => {
    if (score >= 80) return 'Critical';
    if (score >= 60) return 'High';
    if (score >= 40) return 'Medium';
    return 'Low';
  };

  const getVerificationColor = (status) => {
    if (status === 'verified') return 'bg-green-500/20 text-green-400 border-green-500/30';
    if (status === 'pending') return 'bg-yellow-500/20 text-yellow-400 border-yellow-500/30';
    return 'bg-red-500/20 text-red-400 border-red-500/30';
  };

  // Filter assets
  const filteredAssets = assets.filter((asset) => {
    const matchesSearch = asset.domain.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesExposure = exposureFilter === 'all' || 
      (exposureFilter === 'critical' && asset.exposure_score >= 80) ||
      (exposureFilter === 'high' && asset.exposure_score >= 60 && asset.exposure_score < 80) ||
      (exposureFilter === 'medium' && asset.exposure_score >= 40 && asset.exposure_score < 60) ||
      (exposureFilter === 'low' && asset.exposure_score < 40);
    const matchesDomain = domainFilter === 'all' || asset.domain.includes(domainFilter);
    const matchesShadowIt = shadowItFilter === 'all' || 
      (shadowItFilter === 'shadow' && asset.is_shadow_it) ||
      (shadowItFilter === 'known' && !asset.is_shadow_it);
    return matchesSearch && matchesExposure && matchesDomain && matchesShadowIt;
  });

  const filteredDomains = domains.filter((domain) =>
    domain.domain.toLowerCase().includes(searchTerm.toLowerCase())
  );

  // Get unique domains for filter
  const uniqueDomains = [...new Set(assets.map(a => {
    const parts = a.domain.split('.');
    return parts.slice(-2).join('.');
  }))];

  // Generate CSV Report
  const generateCSVReport = () => {
    // Build CSV content
    let csvContent = 'Domain,Asset Type,IP Address,Exposure Score,Exposure Level,Shadow IT,Discovered At,Last Scanned\n';
    
    filteredAssets.forEach(asset => {
      csvContent += `"${asset.domain}","${asset.asset_type || 'N/A'}","${asset.ip_address || 'N/A'}",${asset.exposure_score},"${getExposureLabel(asset.exposure_score)}","${asset.is_shadow_it ? 'Yes' : 'No'}","${new Date(asset.discovered_at).toLocaleString()}","${asset.last_scanned ? new Date(asset.last_scanned).toLocaleString() : 'Never'}"\n`;
    });
    
    // Add summary section
    csvContent += '\n\nSUMMARY\n';
    csvContent += `Total Assets,${filteredAssets.length}\n`;
    csvContent += `Shadow IT,${assets.filter(a => a.is_shadow_it).length}\n`;
    csvContent += `Critical Exposure,${assets.filter(a => a.exposure_score >= 80).length}\n`;
    csvContent += `High Exposure,${assets.filter(a => a.exposure_score >= 60 && a.exposure_score < 80).length}\n`;
    csvContent += `Monitored Domains,${domains.length}\n`;
    
    // Add filters applied
    const activeFilters = [];
    if (exposureFilter !== 'all') activeFilters.push(`Exposure: ${exposureFilter}`);
    if (domainFilter !== 'all') activeFilters.push(`Domain: ${domainFilter}`);
    if (shadowItFilter !== 'all') activeFilters.push(`Type: ${shadowItFilter}`);
    if (activeFilters.length > 0) {
      csvContent += `\nFilters Applied,"${activeFilters.join(', ')}"\n`;
    }
    
    // Download
    const blob = new Blob([csvContent], { type: 'text/csv;charset=utf-8;' });
    const url = window.URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `EASM_Report_${new Date().toISOString().split('T')[0]}.csv`;
    link.click();
    window.URL.revokeObjectURL(url);
    toast.success('EASM CSV Report downloaded');
  };

  // Handle tile click to filter
  const handleTileClick = (filterType) => {
    if (tileFilter === filterType) {
      // Clear filter
      setTileFilter(null);
      setExposureFilter('all');
      setShadowItFilter('all');
    } else {
      setTileFilter(filterType);
      // Apply specific filter based on tile
      if (filterType === 'shadow') {
        setShadowItFilter('shadow');
        setExposureFilter('all');
      } else if (filterType === 'critical') {
        setExposureFilter('critical');
        setShadowItFilter('all');
      } else if (filterType === 'verified') {
        setActiveTab('domains');
      }
    }
  };

  // Get exposure remediation steps
  const getRemediationSteps = (score) => {
    if (score >= 80) {
      return [
        { step: 'Immediate Action Required', desc: 'Address critical vulnerabilities within 24-48 hours' },
        { step: 'Patch Management', desc: 'Apply security patches for known CVEs' },
        { step: 'Access Control Review', desc: 'Restrict access to affected systems' },
        { step: 'Incident Response', desc: 'Prepare incident response procedures' },
        { step: 'Continuous Monitoring', desc: 'Enable real-time threat detection' }
      ];
    } else if (score >= 60) {
      return [
        { step: 'Priority Remediation', desc: 'Address high-risk vulnerabilities within 7 days' },
        { step: 'Configuration Hardening', desc: 'Apply security configurations' },
        { step: 'Network Segmentation', desc: 'Isolate critical assets' },
        { step: 'Regular Scanning', desc: 'Schedule weekly vulnerability scans' }
      ];
    } else if (score >= 40) {
      return [
        { step: 'Scheduled Patching', desc: 'Include in regular patch cycle' },
        { step: 'Security Training', desc: 'Educate users on security best practices' },
        { step: 'Policy Review', desc: 'Review and update security policies' }
      ];
    }
    return [
      { step: 'Maintain Posture', desc: 'Continue regular security maintenance' },
      { step: 'Monitoring', desc: 'Keep automated monitoring active' }
    ];
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-indigo-400 font-rajdhani text-lg">Loading external assets...</div>
      </div>
    );
  }

  return (
    <div data-testid="easm-container" className="space-y-5">
      <div className="flex items-center justify-between">
        <div>
          <h1 className={`font-rajdhani font-bold text-3xl ${isDarkMode ? 'text-white text-shadow-neon' : 'text-slate-900'}`}>
            External Attack Surface Management
          </h1>
          <p className={`font-inter text-sm mt-1 ${isDarkMode ? 'text-zinc-400' : 'text-slate-500'}`}>
            Continuous discovery of internet-facing assets and shadow IT
          </p>
        </div>

        <div className="flex items-center gap-3">
          <Button
            onClick={generateCSVReport}
            variant="outline"
            className="border-emerald-500/30 text-emerald-400 hover:bg-emerald-500/10"
          >
            <FileSpreadsheet className="w-4 h-4 mr-2" />
            Export CSV
          </Button>
          
          <Dialog open={addDomainDialogOpen} onOpenChange={setAddDomainDialogOpen}>
            <DialogTrigger asChild>
              <Button className="bg-emerald-600 hover:bg-emerald-500 font-rajdhani text-sm">
                <Plus className="w-3.5 h-3.5 mr-2" />
                Add Domain
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-zinc-900 border-white/10 text-white">
              <DialogHeader>
                <DialogTitle className="font-rajdhani text-2xl flex items-center gap-2">
                  <Globe className="w-6 h-6 text-emerald-400" />
                  Add Domain for Monitoring
                </DialogTitle>
              </DialogHeader>
              <form onSubmit={handleAddDomain} className="space-y-4 mt-4">
                <div>
                  <Label className="text-zinc-300">Domain</Label>
                  <Input
                    value={newDomain.domain}
                    onChange={(e) => setNewDomain({ ...newDomain, domain: e.target.value })}
                    placeholder="example.com"
                    className="bg-zinc-950/50 border-white/10 text-white mt-2"
                    required
                  />
                </div>
                <div>
                  <Label className="text-zinc-300">Description (Optional)</Label>
                  <Input
                    value={newDomain.description}
                    onChange={(e) => setNewDomain({ ...newDomain, description: e.target.value })}
                    placeholder="Production website"
                    className="bg-zinc-950/50 border-white/10 text-white mt-2"
                  />
                </div>
                <Button
                  type="submit"
                  disabled={addingDomain}
                  className="w-full bg-emerald-600 hover:bg-emerald-500"
                >
                  {addingDomain ? 'Adding...' : 'Add Domain'}
                </Button>
              </form>
            </DialogContent>
          </Dialog>

          <Button
            data-testid="discover-assets-button"
            onClick={handleDiscovery}
            disabled={discovering}
            className="bg-indigo-600 hover:bg-indigo-500 font-rajdhani text-sm"
          >
            <Sparkles className="w-3.5 h-3.5 mr-2" />
            {discovering ? 'Discovering...' : 'Discover Assets'}
          </Button>
        </div>
      </div>

      {/* Stats Cards - Clickable */}
      <div className="grid grid-cols-1 md:grid-cols-5 gap-4">
        <div 
          onClick={() => { setTileFilter(null); setExposureFilter('all'); setShadowItFilter('all'); }}
          className={`glass rounded-xl p-4 cursor-pointer transition-all hover:scale-105 ${
            !tileFilter ? 'ring-2 ring-indigo-500/50' : 'hover:border-indigo-500/30'
          }`}
        >
          <div className="flex items-center gap-3 mb-2">
            <div className="w-9 h-9 bg-indigo-600/20 border border-indigo-500/30 rounded-lg flex items-center justify-center">
              <Globe className="w-4 h-4 text-indigo-400" />
            </div>
            <div>
              <p className="text-zinc-400 text-xs">Total Exposed</p>
              <p className="text-white font-rajdhani text-xl font-bold">{assets.length}</p>
            </div>
          </div>
        </div>

        <div 
          onClick={() => { setActiveTab('domains'); setTileFilter(null); }}
          className="glass rounded-xl p-4 cursor-pointer transition-all hover:scale-105 hover:border-emerald-500/30"
        >
          <div className="flex items-center gap-3 mb-2">
            <div className="w-9 h-9 bg-emerald-600/20 border border-emerald-500/30 rounded-lg flex items-center justify-center">
              <Shield className="w-4 h-4 text-emerald-400" />
            </div>
            <div>
              <p className="text-zinc-400 text-xs">Monitored Domains</p>
              <p className="text-white font-rajdhani text-xl font-bold">{domains.length}</p>
            </div>
          </div>
        </div>

        <div 
          onClick={() => handleTileClick('shadow')}
          className={`glass rounded-xl p-4 cursor-pointer transition-all hover:scale-105 ${
            tileFilter === 'shadow' ? 'ring-2 ring-red-500/50' : 'hover:border-red-500/30'
          }`}
        >
          <div className="flex items-center gap-3 mb-2">
            <div className="w-9 h-9 bg-red-600/20 border border-red-500/30 rounded-lg flex items-center justify-center">
              <AlertTriangle className="w-4 h-4 text-red-400" />
            </div>
            <div>
              <p className="text-zinc-400 text-xs">Shadow IT</p>
              <p className="text-white font-rajdhani text-xl font-bold">
                {assets.filter(a => a.is_shadow_it).length}
              </p>
            </div>
          </div>
        </div>

        <div 
          onClick={() => handleTileClick('critical')}
          className={`glass rounded-xl p-4 cursor-pointer transition-all hover:scale-105 ${
            tileFilter === 'critical' ? 'ring-2 ring-orange-500/50' : 'hover:border-orange-500/30'
          }`}
        >
          <div className="flex items-center gap-3 mb-2">
            <div className="w-9 h-9 bg-orange-600/20 border border-orange-500/30 rounded-lg flex items-center justify-center">
              <Shield className="w-4 h-4 text-orange-400" />
            </div>
            <div>
              <p className="text-zinc-400 text-xs">Critical Exposure</p>
              <p className="text-white font-rajdhani text-xl font-bold">
                {assets.filter(a => a.exposure_score >= 80).length}
              </p>
            </div>
          </div>
        </div>

        <div 
          onClick={() => { setActiveTab('domains'); setTileFilter('verified'); }}
          className={`glass rounded-xl p-4 cursor-pointer transition-all hover:scale-105 ${
            tileFilter === 'verified' ? 'ring-2 ring-green-500/50' : 'hover:border-green-500/30'
          }`}
        >
          <div className="flex items-center gap-3 mb-2">
            <div className="w-9 h-9 bg-green-600/20 border border-green-500/30 rounded-lg flex items-center justify-center">
              <CheckCircle className="w-4 h-4 text-green-400" />
            </div>
            <div>
              <p className="text-zinc-400 text-xs">Verified</p>
              <p className="text-white font-rajdhani text-xl font-bold">
                {domains.filter(d => d.verification_status === 'verified').length}
              </p>
            </div>
          </div>
        </div>
      </div>

      {/* Enterprise EASM Scanner */}
      <div className={`rounded-xl border p-5 ${isDarkMode ? 'bg-zinc-900/40 border-white/10' : 'bg-white border-slate-200 shadow-sm'}`}>
        <div className="flex flex-col lg:flex-row lg:items-end gap-4">
          <div className="flex items-center gap-3">
            <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isDarkMode ? 'bg-purple-500/20 border border-purple-500/30' : 'bg-purple-100 border border-purple-200'}`}>
              <Radar className={`w-5 h-5 ${isDarkMode ? 'text-purple-400' : 'text-purple-600'}`} />
            </div>
            <div>
              <h3 className={`font-semibold ${isDarkMode ? 'text-white' : 'text-slate-900'}`}>Enterprise Attack Surface Scan</h3>
              <p className={`text-xs ${isDarkMode ? 'text-zinc-500' : 'text-slate-500'}`}>Comprehensive external asset discovery & risk analysis</p>
            </div>
          </div>

          <div className="flex-1 flex flex-col sm:flex-row items-start sm:items-end gap-4">
            <div className="flex-1 w-full sm:max-w-xs">
              <Label className={`text-xs ${isDarkMode ? 'text-zinc-300' : 'text-slate-600'}`}>Target Domain</Label>
              {domains.length > 0 ? (
                <Select 
                  value={easmTarget} 
                  onValueChange={setEasmTarget}
                >
                  <SelectTrigger className={`mt-1 text-sm ${isDarkMode ? 'bg-zinc-950/50 border-white/10 text-white' : ''}`}>
                    <SelectValue placeholder="Select monitored domain" />
                  </SelectTrigger>
                  <SelectContent className={isDarkMode ? 'bg-zinc-900 border-white/10' : ''}>
                    {domains.map(d => (
                      <SelectItem key={d.id} value={d.domain}>{d.domain}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              ) : (
                <div className={`mt-1 p-3 rounded-lg text-sm ${isDarkMode ? 'bg-zinc-800/50 text-zinc-400 border border-white/10' : 'bg-slate-50 text-slate-500'}`}>
                  Add a monitored domain first to run scans
                </div>
              )}
            </div>

            <div className="flex items-center gap-3">
              <div className="flex items-center gap-2">
                <Switch
                  checked={easmDeepScan}
                  onCheckedChange={setEasmDeepScan}
                  data-testid="enterprise-easm-deep-scan"
                />
                <Label className={`text-xs ${isDarkMode ? 'text-zinc-300' : 'text-slate-600'}`}>Deep Scan</Label>
              </div>

              <Button 
                onClick={handleEnterpriseEasmScan}
                disabled={runningEasmScan || !easmTarget || domains.length === 0}
                className="bg-purple-600 hover:bg-purple-500"
                data-testid="run-enterprise-easm-btn"
              >
                {runningEasmScan ? (
                  <>
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                    Scanning...
                  </>
                ) : (
                  <>
                    <Radar className="w-4 h-4 mr-2" />
                    Scan Attack Surface
                  </>
                )}
              </Button>
            </div>
          </div>
        </div>

        {/* Recent Enterprise EASM Scans */}
        {easmHistory.length > 0 && (
          <div className="mt-4 pt-4 border-t border-white/10">
            <h4 className={`text-xs font-medium mb-2 ${isDarkMode ? 'text-zinc-400' : 'text-slate-600'}`}>Recent Enterprise Scans</h4>
            <div className="flex flex-wrap gap-2">
              {easmHistory.slice(0, 5).map(scan => (
                <div key={scan.id} className={`px-3 py-2 rounded-lg text-xs flex items-center gap-2 ${isDarkMode ? 'bg-zinc-800' : 'bg-slate-50'}`}>
                  <span className={isDarkMode ? 'text-white' : 'text-slate-900'}>{scan.target_domain}</span>
                  <span className={`px-1.5 py-0.5 rounded ${
                    scan.risk_score >= 70 ? 'bg-red-500/20 text-red-400' :
                    scan.risk_score >= 40 ? 'bg-orange-500/20 text-orange-400' :
                    'bg-green-500/20 text-green-400'
                  }`}>
                    {scan.assets_count || 0} assets
                  </span>
                </div>
              ))}
            </div>
          </div>
        )}
      </div>

      <Tabs value={activeTab} onValueChange={setActiveTab} className="w-full">
        <TabsList className="bg-zinc-900/50 border border-white/10">
          <TabsTrigger 
            value="assets" 
            className="data-[state=active]:bg-indigo-600/20 data-[state=active]:text-indigo-400"
          >
            <Globe className="w-4 h-4 mr-2" />
            Discovered Assets ({assets.length})
          </TabsTrigger>
          <TabsTrigger 
            value="domains" 
            className="data-[state=active]:bg-emerald-600/20 data-[state=active]:text-emerald-400"
          >
            <Shield className="w-4 h-4 mr-2" />
            Monitored Domains ({domains.length})
          </TabsTrigger>
        </TabsList>

        <TabsContent value="assets" className="mt-5">
          <div className="glass rounded-2xl p-5 overflow-x-auto">
            {/* Search and Filters */}
            <div className="flex flex-col lg:flex-row gap-4 mb-4 min-w-fit">
              <div className="relative flex-1 min-w-[200px]">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <Input
                  data-testid="search-easm-input"
                  placeholder="Search domains..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 bg-zinc-950/50 border-white/10 text-white text-sm"
                />
              </div>
              
              <div className="flex gap-2 flex-wrap">
                <Select value={exposureFilter} onValueChange={setExposureFilter}>
                  <SelectTrigger className="w-[140px] bg-zinc-950/50 border-white/10 text-white text-sm">
                    <Filter className="w-3 h-3 mr-2" />
                    <SelectValue placeholder="Exposure" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-white/10 text-white">
                    <SelectItem value="all">All Exposure</SelectItem>
                    <SelectItem value="critical">Critical (80+)</SelectItem>
                    <SelectItem value="high">High (60-79)</SelectItem>
                    <SelectItem value="medium">Medium (40-59)</SelectItem>
                    <SelectItem value="low">Low (&lt;40)</SelectItem>
                  </SelectContent>
                </Select>
                
                <Select value={domainFilter} onValueChange={setDomainFilter}>
                  <SelectTrigger className="w-[160px] bg-zinc-950/50 border-white/10 text-white text-sm">
                    <SelectValue placeholder="Domain" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-white/10 text-white">
                    <SelectItem value="all">All Domains</SelectItem>
                    {uniqueDomains.map(d => (
                      <SelectItem key={d} value={d}>{d}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
                
                <Select value={shadowItFilter} onValueChange={setShadowItFilter}>
                  <SelectTrigger className="w-[140px] bg-zinc-950/50 border-white/10 text-white text-sm">
                    <SelectValue placeholder="Type" />
                  </SelectTrigger>
                  <SelectContent className="bg-zinc-900 border-white/10 text-white">
                    <SelectItem value="all">All Types</SelectItem>
                    <SelectItem value="shadow">Shadow IT Only</SelectItem>
                    <SelectItem value="known">Known Assets</SelectItem>
                  </SelectContent>
                </Select>
                
                {(exposureFilter !== 'all' || domainFilter !== 'all' || shadowItFilter !== 'all') && (
                  <Button
                    variant="ghost"
                    size="sm"
                    onClick={() => {
                      setExposureFilter('all');
                      setDomainFilter('all');
                      setShadowItFilter('all');
                    }}
                    className="text-zinc-400 hover:text-white"
                  >
                    <X className="w-4 h-4 mr-1" />
                    Clear
                  </Button>
                )}
              </div>
            </div>

            <div className="space-y-3">
              {filteredAssets.length === 0 ? (
                <div className="text-center py-10">
                  <Globe className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
                  <p className="text-zinc-400 font-inter text-sm">No external assets found</p>
                  <p className="text-zinc-500 text-xs mt-2">Click "Discover Assets" to start scanning</p>
                </div>
              ) : (
                filteredAssets.map((asset) => (
                  <div
                    key={asset.id}
                    data-testid={`easm-asset-${asset.id}`}
                    onClick={() => openAssetDetail(asset)}
                    className="glass-hover p-4 rounded-lg border border-white/10 hover:border-indigo-500/30 transition-all cursor-pointer"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          {asset.is_shadow_it && (
                            <span className="px-2 py-0.5 bg-red-500/20 text-red-400 border border-red-500/30 rounded text-xs flex items-center gap-1">
                              <AlertTriangle className="w-3 h-3" />
                              Shadow IT
                            </span>
                          )}
                          <span className="px-2 py-1 bg-zinc-700 text-zinc-300 rounded text-xs">{asset.asset_type}</span>
                          <span className={`px-2 py-1 rounded text-xs border ${getExposureColor(asset.exposure_score)}`}>
                            Exposure: {asset.exposure_score}/100 ({getExposureLabel(asset.exposure_score)})
                          </span>
                        </div>
                        
                        <p className="font-mono text-sm text-white mb-2">{asset.domain}</p>
                        
                        {asset.ip_address && (
                          <p className="text-zinc-500 text-xs font-mono">IP: {asset.ip_address}</p>
                        )}
                        
                        <div className="flex items-center gap-4 mt-3 text-xs text-zinc-500">
                          <span>Discovered: {new Date(asset.discovered_at).toLocaleDateString()}</span>
                          {asset.last_scanned && (
                            <span>Last scanned: {new Date(asset.last_scanned).toLocaleDateString()}</span>
                          )}
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-indigo-400 hover:bg-indigo-500/10"
                          onClick={(e) => { e.stopPropagation(); openAssetDetail(asset); }}
                          title="View Details"
                          data-testid={`view-asset-${asset.id}`}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-emerald-400 hover:bg-emerald-500/10"
                          onClick={(e) => { e.stopPropagation(); promoteExternalAsset(asset); }}
                          title="Promote to Inventory"
                          data-testid={`promote-asset-${asset.id}`}
                        >
                          <Plus className="w-4 h-4" />
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-red-400 hover:bg-red-500/10"
                          onClick={(e) => { e.stopPropagation(); deleteExternalAsset(asset.id); }}
                          title="Delete Asset"
                          data-testid={`delete-asset-${asset.id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </Button>
                        <div className="w-10 h-10 bg-cyan-600/20 border border-cyan-500/30 rounded-lg flex items-center justify-center flex-shrink-0">
                          <Globe className="w-5 h-5 text-cyan-400" />
                        </div>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </TabsContent>

        <TabsContent value="domains" className="mt-5">
          <div className="glass rounded-2xl p-5">
            <div className="mb-4">
              <div className="relative">
                <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-zinc-500" />
                <Input
                  placeholder="Search domains..."
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  className="pl-10 bg-zinc-950/50 border-white/10 text-white text-sm"
                />
              </div>
            </div>

            <div className="space-y-3">
              {filteredDomains.length === 0 ? (
                <div className="text-center py-10">
                  <Shield className="w-12 h-12 text-zinc-600 mx-auto mb-3" />
                  <p className="text-zinc-400 font-inter text-sm">No domains being monitored</p>
                  <p className="text-zinc-500 text-xs mt-2">Click "Add Domain" to start monitoring</p>
                </div>
              ) : (
                filteredDomains.map((domain) => (
                  <div
                    key={domain.id}
                    onClick={() => openDomainDetail(domain)}
                    className="glass-hover p-4 rounded-lg border border-white/10 hover:border-emerald-500/30 transition-all cursor-pointer"
                  >
                    <div className="flex items-start justify-between">
                      <div className="flex-1">
                        <div className="flex items-center gap-2 mb-2">
                          <span className={`px-2 py-1 rounded text-xs border ${getVerificationColor(domain.verification_status)}`}>
                            {domain.verification_status === 'verified' ? (
                              <span className="flex items-center gap-1">
                                <CheckCircle className="w-3 h-3" />
                                Verified
                              </span>
                            ) : (
                              'Pending Verification'
                            )}
                          </span>
                          {domain.mapped_asset_id && (
                            <span className="px-2 py-1 bg-blue-500/20 text-blue-400 border border-blue-500/30 rounded text-xs flex items-center gap-1">
                              <Link2 className="w-3 h-3" />
                              Mapped
                            </span>
                          )}
                          {domain.discovered_assets > 0 && (
                            <span className="px-2 py-1 bg-indigo-500/20 text-indigo-400 border border-indigo-500/30 rounded text-xs">
                              {domain.discovered_assets} assets found
                            </span>
                          )}
                        </div>
                        
                        <p className="font-mono text-sm text-white mb-2">{domain.domain}</p>
                        
                        {domain.description && (
                          <p className="text-zinc-400 text-xs mb-2">{domain.description}</p>
                        )}
                        
                        <div className="flex items-center gap-4 mt-3 text-xs text-zinc-500">
                          <span>Added: {new Date(domain.added_at).toLocaleDateString()}</span>
                        </div>
                      </div>
                      
                      <div className="flex items-center gap-2">
                        <Button
                          size="sm"
                          variant="ghost"
                          className="text-emerald-400 hover:bg-emerald-500/10"
                          onClick={(e) => { e.stopPropagation(); openDomainDetail(domain); }}
                        >
                          <Eye className="w-4 h-4" />
                        </Button>
                        {domain.verification_status !== 'verified' && (
                          <Button
                            size="sm"
                            variant="outline"
                            onClick={(e) => { e.stopPropagation(); handleVerifyDomain(domain); }}
                            className="border-green-500/30 text-green-400 hover:bg-green-500/10"
                          >
                            <CheckCircle className="w-3 h-3 mr-1" />
                            Verify
                          </Button>
                        )}
                        <Button
                          size="sm"
                          variant="outline"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedDomain(domain);
                            setMapAssetDialogOpen(true);
                          }}
                          className="border-blue-500/30 text-blue-400 hover:bg-blue-500/10"
                        >
                          <Link2 className="w-3 h-3 mr-1" />
                          Map
                        </Button>
                        <Button
                          size="sm"
                          variant="ghost"
                          onClick={(e) => {
                            e.stopPropagation();
                            setSelectedDomain(domain);
                            setDeleteDomainDialogOpen(true);
                          }}
                          className="text-red-400 hover:bg-red-500/10"
                        >
                          <Trash2 className="w-3 h-3" />
                        </Button>
                      </div>
                    </div>
                  </div>
                ))
              )}
            </div>
          </div>
        </TabsContent>
      </Tabs>

      {/* Asset Detail Dialog */}
      <Dialog open={assetDetailOpen} onOpenChange={setAssetDetailOpen}>
        <DialogContent className="bg-zinc-900 border-white/10 text-white max-w-2xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-rajdhani text-2xl flex items-center gap-2">
              <Globe className="w-6 h-6 text-cyan-400" />
              Asset Details
            </DialogTitle>
          </DialogHeader>
          
          {selectedAsset && (
            <div className="space-y-4 mt-4">
              {/* Status badges */}
              <div className="flex flex-wrap gap-2">
                {selectedAsset.is_shadow_it && (
                  <span className="px-3 py-1 bg-red-500/20 text-red-400 border border-red-500/30 rounded-full text-sm flex items-center gap-1">
                    <AlertTriangle className="w-4 h-4" />
                    Shadow IT
                  </span>
                )}
                <span className={`px-3 py-1 rounded-full text-sm border ${getExposureColor(selectedAsset.exposure_score)}`}>
                  Exposure: {selectedAsset.exposure_score}/100 ({getExposureLabel(selectedAsset.exposure_score)})
                </span>
                <span className="px-3 py-1 bg-zinc-700 text-zinc-300 rounded-full text-sm">
                  {selectedAsset.asset_type}
                </span>
              </div>
              
              {/* Main info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-zinc-800/50 rounded-lg">
                  <p className="text-zinc-500 text-xs mb-1">Domain</p>
                  <p className="text-white font-mono text-sm flex items-center gap-2">
                    {selectedAsset.domain}
                    <a href={`https://${selectedAsset.domain}`} target="_blank" rel="noopener noreferrer" className="text-indigo-400 hover:text-indigo-300">
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  </p>
                </div>
                
                <div className="p-4 bg-zinc-800/50 rounded-lg">
                  <p className="text-zinc-500 text-xs mb-1">IP Address</p>
                  <p className="text-white font-mono text-sm">{selectedAsset.ip_address || 'N/A'}</p>
                </div>
                
                <div className="p-4 bg-zinc-800/50 rounded-lg">
                  <p className="text-zinc-500 text-xs mb-1">Discovered</p>
                  <p className="text-white text-sm">{new Date(selectedAsset.discovered_at).toLocaleString()}</p>
                </div>
                
                <div className="p-4 bg-zinc-800/50 rounded-lg">
                  <p className="text-zinc-500 text-xs mb-1">Last Scanned</p>
                  <p className="text-white text-sm">{selectedAsset.last_scanned ? new Date(selectedAsset.last_scanned).toLocaleString() : 'Never'}</p>
                </div>
              </div>
              
              {/* Open Ports */}
              {selectedAsset.open_ports && selectedAsset.open_ports.length > 0 && (
                <div className="p-4 bg-zinc-800/50 rounded-lg">
                  <p className="text-zinc-500 text-xs mb-2">Open Ports</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedAsset.open_ports.map(port => (
                      <span key={port} className="px-2 py-1 bg-orange-500/20 text-orange-400 border border-orange-500/30 rounded text-xs font-mono">
                        {port}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Technologies */}
              {selectedAsset.technologies && selectedAsset.technologies.length > 0 && (
                <div className="p-4 bg-zinc-800/50 rounded-lg">
                  <p className="text-zinc-500 text-xs mb-2">Technologies Detected</p>
                  <div className="flex flex-wrap gap-2">
                    {selectedAsset.technologies.map(tech => (
                      <span key={tech} className="px-2 py-1 bg-indigo-500/20 text-indigo-400 border border-indigo-500/30 rounded text-xs">
                        {tech}
                      </span>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Identified Issues & Remediation Steps */}
              {selectedAsset && (
                <div className="p-4 bg-gradient-to-br from-indigo-900/30 to-violet-900/30 border border-indigo-500/30 rounded-lg">
                  <p className="text-indigo-400 font-semibold mb-3 flex items-center gap-2">
                    <Lightbulb className="w-4 h-4" />
                    Security Analysis & Remediation
                  </p>
                  
                  {/* Exposure Analysis */}
                  <div className="grid grid-cols-2 gap-3 mb-4">
                    <div className="p-3 bg-zinc-900/50 rounded-lg">
                      <p className="text-zinc-500 text-xs">Exposure Score</p>
                      <p className={`text-2xl font-bold ${
                        selectedAsset.exposure_score >= 80 ? 'text-red-400' :
                        selectedAsset.exposure_score >= 60 ? 'text-orange-400' :
                        selectedAsset.exposure_score >= 40 ? 'text-yellow-400' : 'text-green-400'
                      }`}>
                        {selectedAsset.exposure_score}/100
                      </p>
                      <p className="text-zinc-500 text-xs mt-1">{getExposureLabel(selectedAsset.exposure_score)}</p>
                    </div>
                    <div className="p-3 bg-zinc-900/50 rounded-lg">
                      <p className="text-zinc-500 text-xs">Risk Level</p>
                      <p className={`text-lg font-bold ${
                        selectedAsset.exposure_score >= 80 ? 'text-red-400' :
                        selectedAsset.exposure_score >= 60 ? 'text-orange-400' :
                        selectedAsset.exposure_score >= 40 ? 'text-yellow-400' : 'text-green-400'
                      }`}>
                        {selectedAsset.exposure_score >= 80 ? 'CRITICAL' :
                         selectedAsset.exposure_score >= 60 ? 'HIGH' :
                         selectedAsset.exposure_score >= 40 ? 'MEDIUM' : 'LOW'}
                      </p>
                      <p className="text-zinc-500 text-xs mt-1">
                        {selectedAsset.exposure_score >= 80 ? 'Immediate action required' :
                         selectedAsset.exposure_score >= 60 ? 'Priority remediation needed' :
                         selectedAsset.exposure_score >= 40 ? 'Schedule for remediation' : 'Continue monitoring'}
                      </p>
                    </div>
                  </div>

                  {/* Identified Issues */}
                  <div className="mb-4">
                    <p className="text-zinc-300 text-sm font-medium mb-2">Identified Issues:</p>
                    <div className="space-y-2">
                      {selectedAsset.open_ports && selectedAsset.open_ports.length > 0 && (
                        <div className="flex items-start gap-2 p-2 bg-zinc-900/50 rounded">
                          <AlertTriangle className="w-4 h-4 text-orange-400 mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="text-white text-sm font-medium">Open Ports Detected</p>
                            <p className="text-zinc-500 text-xs">Ports {selectedAsset.open_ports.join(', ')} are exposed to the internet. Review if these services need public access.</p>
                          </div>
                        </div>
                      )}
                      {selectedAsset.is_shadow_it && (
                        <div className="flex items-start gap-2 p-2 bg-zinc-900/50 rounded">
                          <AlertTriangle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="text-white text-sm font-medium">Shadow IT Detected</p>
                            <p className="text-zinc-500 text-xs">This asset was discovered outside official IT inventory. Verify ownership and ensure proper security controls.</p>
                          </div>
                        </div>
                      )}
                      {(!selectedAsset.last_scanned || new Date() - new Date(selectedAsset.last_scanned) > 30*24*60*60*1000) && (
                        <div className="flex items-start gap-2 p-2 bg-zinc-900/50 rounded">
                          <AlertTriangle className="w-4 h-4 text-yellow-400 mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="text-white text-sm font-medium">Outdated Scan Data</p>
                            <p className="text-zinc-500 text-xs">Last scan was over 30 days ago. Schedule a new security scan to ensure current status.</p>
                          </div>
                        </div>
                      )}
                      {selectedAsset.exposure_score >= 60 && (
                        <div className="flex items-start gap-2 p-2 bg-zinc-900/50 rounded">
                          <AlertTriangle className="w-4 h-4 text-red-400 mt-0.5 flex-shrink-0" />
                          <div>
                            <p className="text-white text-sm font-medium">High Exposure Risk</p>
                            <p className="text-zinc-500 text-xs">This asset has elevated exposure. Review security configurations and access controls immediately.</p>
                          </div>
                        </div>
                      )}
                    </div>
                  </div>
                  
                  {/* Remediation Steps */}
                  <div className="space-y-2">
                    <p className="text-zinc-300 text-sm font-medium">Recommended Actions:</p>
                    {getRemediationSteps(selectedAsset.exposure_score).map((item, idx) => (
                      <div key={idx} className="flex items-start gap-2 p-2 bg-zinc-900/50 rounded">
                        <ArrowRight className="w-4 h-4 text-indigo-400 mt-0.5 flex-shrink-0" />
                        <div>
                          <p className="text-white text-sm font-medium">{item.step}</p>
                          <p className="text-zinc-500 text-xs">{item.desc}</p>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
              
              {/* Related Threats */}
              {threats.filter(t => t.source?.includes(selectedAsset.domain)).length > 0 && (
                <div className="p-4 bg-red-900/20 border border-red-500/30 rounded-lg">
                  <p className="text-red-400 text-sm font-semibold mb-2 flex items-center gap-2">
                    <Activity className="w-4 h-4" />
                    Related Threats ({threats.filter(t => t.source?.includes(selectedAsset.domain)).length})
                  </p>
                  <div className="space-y-2">
                    {threats.filter(t => t.source?.includes(selectedAsset.domain)).map(threat => (
                      <div key={threat.id} className="p-3 bg-zinc-800/50 rounded-lg border border-red-500/20">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-white text-sm font-medium">{threat.name}</p>
                            <p className="text-zinc-500 text-xs mt-1">{threat.description?.substring(0, 100)}...</p>
                          </div>
                          <span className={`px-2 py-1 rounded text-xs ${
                            threat.severity === 'critical' ? 'bg-red-500/20 text-red-400' :
                            threat.severity === 'high' ? 'bg-orange-500/20 text-orange-400' :
                            'bg-yellow-500/20 text-yellow-400'
                          }`}>
                            {threat.severity}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Domain Detail Dialog */}
      <Dialog open={domainDetailOpen} onOpenChange={setDomainDetailOpen}>
        <DialogContent className="bg-zinc-900 border-white/10 text-white max-w-3xl max-h-[80vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="font-rajdhani text-2xl flex items-center gap-2">
              <Shield className="w-6 h-6 text-emerald-400" />
              Domain Details
            </DialogTitle>
          </DialogHeader>
          
          {selectedDomain && (
            <div className="space-y-4 mt-4">
              {/* Status badges */}
              <div className="flex flex-wrap gap-2">
                <span className={`px-3 py-1 rounded-full text-sm border ${getVerificationColor(selectedDomain.verification_status)}`}>
                  {selectedDomain.verification_status === 'verified' ? 'Verified' : 'Pending Verification'}
                </span>
                {selectedDomain.mapped_asset_id && (
                  <span className="px-3 py-1 bg-blue-500/20 text-blue-400 border border-blue-500/30 rounded-full text-sm flex items-center gap-1">
                    <Link2 className="w-4 h-4" />
                    Mapped to Asset
                  </span>
                )}
              </div>
              
              {/* Main info */}
              <div className="grid grid-cols-2 gap-4">
                <div className="p-4 bg-zinc-800/50 rounded-lg">
                  <p className="text-zinc-500 text-xs mb-1">Domain</p>
                  <p className="text-white font-mono text-sm flex items-center gap-2">
                    {selectedDomain.domain}
                    <a href={`https://${selectedDomain.domain}`} target="_blank" rel="noopener noreferrer" className="text-emerald-400 hover:text-emerald-300">
                      <ExternalLink className="w-3 h-3" />
                    </a>
                  </p>
                </div>
                
                <div className="p-4 bg-zinc-800/50 rounded-lg">
                  <p className="text-zinc-500 text-xs mb-1">Added</p>
                  <p className="text-white text-sm">{new Date(selectedDomain.added_at).toLocaleString()}</p>
                </div>
              </div>
              
              {selectedDomain.description && (
                <div className="p-4 bg-zinc-800/50 rounded-lg">
                  <p className="text-zinc-500 text-xs mb-1">Description</p>
                  <p className="text-white text-sm">{selectedDomain.description}</p>
                </div>
              )}
              
              {/* Discovered Assets for this domain */}
              <div className="p-4 bg-zinc-800/50 rounded-lg">
                <p className="text-zinc-300 font-semibold mb-3 flex items-center gap-2">
                  <Globe className="w-4 h-4 text-indigo-400" />
                  Discovered Assets ({getAssetsForDomain(selectedDomain.domain).length})
                </p>
                
                {getAssetsForDomain(selectedDomain.domain).length === 0 ? (
                  <p className="text-zinc-500 text-sm">No assets discovered for this domain yet.</p>
                ) : (
                  <div className="space-y-2 max-h-60 overflow-y-auto">
                    {getAssetsForDomain(selectedDomain.domain).map(asset => (
                      <div key={asset.id} className="p-3 bg-zinc-900/50 rounded-lg border border-white/5">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-white font-mono text-sm">{asset.domain}</p>
                            <div className="flex items-center gap-2 mt-1">
                              <span className="text-zinc-500 text-xs">{asset.asset_type}</span>
                              {asset.ip_address && <span className="text-zinc-500 text-xs">• {asset.ip_address}</span>}
                            </div>
                          </div>
                          <span className={`px-2 py-1 rounded text-xs border ${getExposureColor(asset.exposure_score)}`}>
                            {asset.exposure_score}/100
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                )}
              </div>
              
              {/* Exposure Values & Remediation Steps */}
              {getAssetsForDomain(selectedDomain.domain).length > 0 && (
                <div className="p-4 bg-gradient-to-br from-indigo-900/30 to-violet-900/30 border border-indigo-500/30 rounded-lg">
                  <p className="text-indigo-400 font-semibold mb-3 flex items-center gap-2">
                    <Lightbulb className="w-4 h-4" />
                    Exposure Analysis & Remediation Steps
                  </p>
                  
                  {/* Calculate average exposure for domain */}
                  {(() => {
                    const domainAssets = getAssetsForDomain(selectedDomain.domain);
                    const avgExposure = domainAssets.length > 0 
                      ? Math.round(domainAssets.reduce((sum, a) => sum + a.exposure_score, 0) / domainAssets.length)
                      : 0;
                    const maxExposure = domainAssets.length > 0
                      ? Math.max(...domainAssets.map(a => a.exposure_score))
                      : 0;
                    
                    return (
                      <>
                        <div className="grid grid-cols-2 gap-3 mb-4">
                          <div className="p-3 bg-zinc-900/50 rounded-lg">
                            <p className="text-zinc-500 text-xs">Average Exposure</p>
                            <p className={`text-2xl font-bold ${
                              avgExposure >= 80 ? 'text-red-400' :
                              avgExposure >= 60 ? 'text-orange-400' :
                              avgExposure >= 40 ? 'text-yellow-400' : 'text-green-400'
                            }`}>
                              {avgExposure}/100
                            </p>
                            <p className="text-zinc-500 text-xs mt-1">{getExposureLabel(avgExposure)}</p>
                          </div>
                          <div className="p-3 bg-zinc-900/50 rounded-lg">
                            <p className="text-zinc-500 text-xs">Highest Exposure</p>
                            <p className={`text-2xl font-bold ${
                              maxExposure >= 80 ? 'text-red-400' :
                              maxExposure >= 60 ? 'text-orange-400' :
                              maxExposure >= 40 ? 'text-yellow-400' : 'text-green-400'
                            }`}>
                              {maxExposure}/100
                            </p>
                            <p className="text-zinc-500 text-xs mt-1">{getExposureLabel(maxExposure)}</p>
                          </div>
                        </div>
                        
                        <div className="space-y-2">
                          <p className="text-zinc-300 text-sm font-medium">Recommended Actions:</p>
                          {getRemediationSteps(maxExposure).map((item, idx) => (
                            <div key={idx} className="flex items-start gap-2 p-2 bg-zinc-900/50 rounded">
                              <ArrowRight className="w-4 h-4 text-indigo-400 mt-0.5 flex-shrink-0" />
                              <div>
                                <p className="text-white text-sm font-medium">{item.step}</p>
                                <p className="text-zinc-500 text-xs">{item.desc}</p>
                              </div>
                            </div>
                          ))}
                        </div>
                      </>
                    );
                  })()}
                </div>
              )}
              
              {/* Related Threats */}
              {getThreatsForDomain(selectedDomain.domain).length > 0 && (
                <div className="p-4 bg-red-900/20 border border-red-500/30 rounded-lg">
                  <p className="text-red-400 font-semibold mb-3 flex items-center gap-2">
                    <Activity className="w-4 h-4" />
                    Related Threats ({getThreatsForDomain(selectedDomain.domain).length})
                  </p>
                  <div className="space-y-2">
                    {getThreatsForDomain(selectedDomain.domain).map(threat => (
                      <div key={threat.id} className="p-3 bg-zinc-900/50 rounded-lg border border-red-500/20">
                        <div className="flex items-center justify-between">
                          <div>
                            <p className="text-white text-sm">{threat.name}</p>
                            <p className="text-zinc-500 text-xs mt-1">{threat.description?.substring(0, 100)}...</p>
                          </div>
                          <span className={`px-2 py-1 rounded text-xs ${
                            threat.severity === 'critical' ? 'bg-red-500/20 text-red-400' :
                            threat.severity === 'high' ? 'bg-orange-500/20 text-orange-400' :
                            'bg-yellow-500/20 text-yellow-400'
                          }`}>
                            {threat.severity}
                          </span>
                        </div>
                      </div>
                    ))}
                  </div>
                </div>
              )}
            </div>
          )}
        </DialogContent>
      </Dialog>

      {/* Map Asset Dialog */}
      <Dialog open={mapAssetDialogOpen} onOpenChange={setMapAssetDialogOpen}>
        <DialogContent className="bg-zinc-900 border-white/10 text-white">
          <DialogHeader>
            <DialogTitle className="font-rajdhani text-2xl flex items-center gap-2">
              <Link2 className="w-6 h-6 text-blue-400" />
              Map Domain to Asset
            </DialogTitle>
          </DialogHeader>
          <div className="space-y-4 mt-4">
            <div className="p-3 bg-zinc-800/50 rounded-lg">
              <p className="text-zinc-400 text-xs mb-1">Domain</p>
              <p className="text-white font-mono">{selectedDomain?.domain}</p>
            </div>
            
            <div>
              <Label className="text-zinc-300">Select Internal Asset</Label>
              <Select value={selectedAssetToMap} onValueChange={setSelectedAssetToMap}>
                <SelectTrigger className="bg-zinc-950/50 border-white/10 text-white mt-2">
                  <SelectValue placeholder="Select an asset..." />
                </SelectTrigger>
                <SelectContent className="bg-zinc-900 border-white/10 text-white">
                  {internalAssets.map(asset => (
                    <SelectItem key={asset.id} value={asset.id}>
                      <div className="flex items-center gap-2">
                        <Server className="w-3 h-3 text-zinc-400" />
                        {asset.name} ({asset.type})
                      </div>
                    </SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            
            <Button
              onClick={handleMapAsset}
              disabled={!selectedAssetToMap}
              className="w-full bg-blue-600 hover:bg-blue-500"
            >
              <Link2 className="w-4 h-4 mr-2" />
              Map Domain to Asset
            </Button>
          </div>
        </DialogContent>
      </Dialog>

      {/* Delete Domain Confirmation */}
      <AlertDialog open={deleteDomainDialogOpen} onOpenChange={setDeleteDomainDialogOpen}>
        <AlertDialogContent className="bg-zinc-900 border-white/10 text-white">
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Domain?</AlertDialogTitle>
            <AlertDialogDescription className="text-zinc-400">
              Are you sure you want to remove <span className="text-red-400 font-mono">{selectedDomain?.domain}</span> from monitoring?
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel className="bg-zinc-800 text-white border-white/10 hover:bg-zinc-700">
              Cancel
            </AlertDialogCancel>
            <AlertDialogAction
              onClick={handleDeleteDomain}
              className="bg-red-600 hover:bg-red-500 text-white"
            >
              Remove Domain
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </div>
  );
}
